import { useMemo } from "react";

import { selectLayerByVersionAndLayerKey, selectVersionConfig } from "fond/api";
import { MapboxStyleLayer } from "fond/types";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { selectAllStylesByLayer } from "fond/utils/configurations";
import { useAppSelector } from "fond/utils/hooks";

import { addFilterCondition, transformFilterForDrawMode, transformLayerConfigStylesForDrawing } from "../helper";

/**
 * Type guard to check if a config entity is a LayerConfig with Styles property
 */
const isLayerConfig = (config: unknown): config is LayerConfig => {
  return !!config && typeof config === "object" && "Styles" in config && Array.isArray((config as LayerConfig).Styles);
};

/**
 * Type guard to check if a config entity has Styles (LayerConfig or SublayerConfig)
 */
const hasStyles = (config: unknown): config is { Styles: string[] } => {
  return !!config && typeof config === "object" && "Styles" in config && Array.isArray((config as { Styles: string[] }).Styles);
};

/**
 * Helper to apply active state styling to a Mapbox layer style.
 * When a feature has `active: true`, it will be highlighted in the specified color.
 *
 * @param style - The MapboxStyle to apply active styling to
 * @param activeColor - The color to use for active features (default: "yellow")
 * @returns The paint object with active styling applied
 */
const applyActiveStateStyling = (style: Pick<MapboxStyleLayer, "type" | "paint">, activeColor: string = "yellow"): MapboxStyleLayer["paint"] => {
  const { type, paint } = style;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const paintAny = paint as any; // TODO - lets change this to be more type safe

  switch (type) {
    case "symbol":
      return {
        ...paint,
        "text-color": ["case", ["==", ["get", "active"], "true"], activeColor, paintAny?.["text-color"] || "black"],
        "text-halo-color": ["case", ["==", ["get", "active"], "true"], activeColor, paintAny?.["text-halo-color"] || "black"],
      };
    case "line":
      return {
        ...paint,
        "line-color": ["case", ["==", ["get", "active"], "true"], activeColor, paintAny?.["line-color"] || "black"],
      };
    case "circle":
      return {
        ...paint,
        "circle-color": ["case", ["==", ["get", "active"], "true"], activeColor, paintAny?.["circle-color"] || "black"],
      };
    default:
      return paint;
  }
};

/**
 * Custom hook to get drawing styles for multiple layers from the layer configuration.
 *
 * This hook processes all layer keys at once to comply with React's Rules of Hooks.
 *
 * @param versionId - The version ID to get the layers from
 * @param layerKeys - Array of layer keys (e.g., ["input_layers/address", "input_layers/street"])
 * @returns Map of layerKey to MapboxStyleLayer array, or undefined if not available for a layer
 */
export const useMultiLayerDrawingStyles = (versionId: string, layerKeys: string[]): Map<string, MapboxStyleLayer[] | undefined> => {
  const config = useAppSelector((state) => selectVersionConfig(state, versionId));

  // Get all layer data in one selector call
  const layers = useAppSelector((state) => {
    return layerKeys.map((layerKey) => {
      const layer = selectLayerByVersionAndLayerKey(state, { versionId, layerId: layerKey });
      return {
        layerKey,
        layer,
      };
    });
  });

  return useMemo(() => {
    const stylesMap = new Map<string, MapboxStyleLayer[] | undefined>();

    if (!config?.Data?.entities) {
      // Return empty map if we don't have the required data
      layerKeys.forEach((key) => stylesMap.set(key, undefined));
      return stylesMap;
    }

    // Process each layer
    layers.forEach(({ layerKey, layer }) => {
      const layerConfig = layer?.Configuration?.ID ? config.Data.entities[layer.Configuration.ID] : undefined;

      // Check if we have a valid LayerConfig with Styles
      if (!isLayerConfig(layerConfig)) {
        stylesMap.set(layerKey, undefined);
        return;
      }

      // Use the existing selector to get all styles for this layer (including children)
      const layerStyles = selectAllStylesByLayer(layerConfig, config);

      // Build a map of style IDs to their sublayer filters
      const styleIdToFilter = new Map<string, MapboxStyleLayer["filter"]>();

      // Add styles from the main layer (no filter)
      layerConfig.Styles.forEach((styleId) => styleIdToFilter.set(styleId, undefined));

      // Add styles from all children (sublayers) with their filters
      if (layerConfig.Children) {
        layerConfig.Children.forEach((childId) => {
          const childConfig = config.Data.entities[childId];
          if (hasStyles(childConfig)) {
            const isSublayer = "Type" in childConfig && childConfig.Type === "SUBLAYER";
            const sublayerFilter =
              isSublayer && "FilterConfiguration" in childConfig ? (childConfig as SublayerConfig).FilterConfiguration?.Mapbox : undefined;

            const transformedFilter = sublayerFilter ? transformFilterForDrawMode(sublayerFilter) : undefined;

            childConfig.Styles.forEach((styleId) => styleIdToFilter.set(styleId, transformedFilter));
          }
        });
      }

      // Convert LayerStyle objects to MapboxStyleLayer with proper IDs and filters
      const mapboxStyles = layerStyles
        .map((layerStyle) => {
          if (!layerStyle.MapboxStyle) return null;

          const sublayerFilter = styleIdToFilter.get(layerStyle.ID);
          const combinedFilter = sublayerFilter ? addFilterCondition(layerStyle.MapboxStyle.filter, sublayerFilter) : layerStyle.MapboxStyle.filter;

          // Apply active state styling (highlights features with active: true in yellow)
          const paint = applyActiveStateStyling(layerStyle.MapboxStyle);

          return {
            ...layerStyle.MapboxStyle,
            id: layerStyle.ID,
            filter: combinedFilter,
            paint,
          } as MapboxStyleLayer;
        })
        .filter(Boolean) as MapboxStyleLayer[];

      if (mapboxStyles.length === 0) {
        stylesMap.set(layerKey, undefined);
        return;
      }

      // Transform for use with drawing tools
      const transformed = transformLayerConfigStylesForDrawing(layerKey, mapboxStyles);
      stylesMap.set(layerKey, transformed);
    });

    return stylesMap;
  }, [layers, layerKeys, config]);
};
